/**
 * Add a client. On the kit's fields and the Branches dialog's layout since
 * Wave 15B (C14 §10.6 item 7; T-6.14) — what it asks and sends is unchanged.
 *
 * Two things about this form are decisions rather than layout.
 *
 * **The PAN field is optional and looks it** (ADR-0016). It carries no
 * `required` attribute and no red star, and `draftProblems` refuses
 * only a *partially typed* PAN. Leaving `required` on the input would have
 * quietly re-imposed the rule the owner removed — *"PAN should not bound to
 * user because some firm not use it e.g. lawyer, other professionals"* — and
 * the form would have looked correct while making an advocate's client
 * impossible to record.
 *
 * **The kind drives the form** (ADR-0015). Individual: no constitution, no
 * company type. Business: a constitution, and a company type only for a
 * company. The same three rules are enforced by the service; this is the UI
 * half of ADR-0029's "both, always".
 *
 * The failure handling is the third: three refusals are *expected* here and
 * each is a different sentence — `VALIDATION_ERROR` (a typo),
 * `CONFLICT` (this firm already holds that PAN — their own record, safe to
 * name), and `USAGE_LIMIT_EXCEEDED`, which is **not** an error but a commercial
 * fact and must come with a way forward.
 *
 * C27 (spec §10.19): the PAN's fourth character must fit the kind (ADR-0057,
 * UAT report #11) and a name must hold a letter (#18) — both said under the
 * field before anything is sent, and a `VALIDATION_ERROR` naming a field lands
 * under that field too. Required fields carry the red star and nothing marks
 * the optional ones (#19). *Add client* is pressable whenever nothing is being
 * sent: pressing it with a required answer missing says so under the field
 * (#13), where a greyed-out button said nothing at all.
 */

import { Button, Dialog, Input, RadioGroup, Select } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  COMPANY_TYPE_LABELS,
  CONSTITUTION_LABELS,
  type DraftClient,
  type Vocabulary,
  clients,
  companyTypeApplies,
  constitutionRequired,
  draftMessages,
  refusedField,
} from '../../lib/api/clients'
import { APP_SLUG, plansUrl } from '../../lib/config'

const EMPTY: DraftClient = {
  legal_name: '',
  kind: 'individual',
  constitution: null,
  company_type: null,
  pan: '',
  gstin: '',
}

export function NewClientDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [draft, setDraft] = useState<DraftClient>(EMPTY)
  const [tags, setTags] = useState('')
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null)
  const [error, setError] = useState<{ message: string; atCap: boolean } | null>(null)
  /** A service refusal that names a field, shown under it until it is edited. */
  const [refused, setRefused] = useState<{ field: string; message: string } | null>(null)
  /** Whether *Add client* has been pressed — what turns a blank required
   *  field from "not yet" into "missing" (#13). */
  const [attempted, setAttempted] = useState(false)
  const [saving, setSaving] = useState(false)

  // Served, not hard-coded: the form's vocabulary and the repository's are then
  // the same vocabulary, and a new constitution is a deploy of one service.
  useEffect(() => {
    clients
      .vocabulary()
      .then((body) => setVocabulary(body.data))
      .catch(() => setVocabulary(null))
  }, [])

  const messages = draftMessages(draft)
  const set = (changes: Partial<DraftClient>) => {
    if (refused && refused.field in changes) setRefused(null)
    setDraft((d) => ({ ...d, ...changes }))
  }
  /** What to say under a field: a rule broken as typed, a required answer
   *  missing once Save was pressed, or the service's own refusal. */
  const fieldError = (field: keyof DraftClient): string | null => {
    if (refused?.field === field) return refused.message
    const message = messages[field]
    if (!message) return null
    const missing =
      (field === 'legal_name' && !draft.legal_name.trim()) ||
      (field === 'constitution' && !draft.constitution)
    return missing && !attempted ? null : message
  }

  const setKind = (kind: string) =>
    // Clearing both on the way past is what keeps a half-converted draft from
    // being submitted: a business's constitution left on an individual is
    // refused by the service, and the user would not know which field to fix.
    set({ kind, constitution: null, company_type: null })

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setAttempted(true)
    if (Object.keys(messages).length > 0) return
    setSaving(true)
    setError(null)
    setRefused(null)
    try {
      await clients.create({
        legal_name: draft.legal_name.trim(),
        kind: draft.kind as 'individual' | 'business',
        constitution: draft.constitution,
        company_type: draft.company_type,
        // `|| null`, never `|| undefined`: an empty box means *no PAN*, which
        // is a real answer the service accepts.
        pan: draft.pan.trim().toUpperCase() || null,
        gstin: draft.gstin.trim().toUpperCase() || null,
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      })
      onCreated()
    } catch (err) {
      const onField = refusedField(err)
      if (onField && onField.field in draft) {
        setRefused(onField)
      } else if (err instanceof ApiError) {
        setError({ message: err.message, atCap: err.code === 'USAGE_LIMIT_EXCEEDED' })
      } else {
        setError({ message: 'The client could not be added.', atCap: false })
      }
    } finally {
      setSaving(false)
    }
  }

  const constitutions = vocabulary?.constitutions ?? Object.keys(CONSTITUTION_LABELS)
  const companyTypes = vocabulary?.company_types ?? Object.keys(COMPANY_TYPE_LABELS)
  const panHint = fieldError('pan')
    ? undefined
    : 'Leave this empty if the client has no PAN, or you don’t have it yet.'
  const gstinHint =
    !fieldError('gstin') && draft.gstin.trim() && !draft.pan.trim()
      ? 'We’ll take the PAN from this GSTIN.'
      : undefined

  return (
    // The kit's dialog: the panel is bounded by the window and its body
    // scrolls, so the title and the close button stay reachable.
    <Dialog open onClose={onClose} title="Add a client" error={error?.message} width="lg">
      {/* `noValidate`: the browser's own bubble would answer a missing
          required field before `submit` ran, and say it in the browser's words
          rather than under the field (#13). */}
      <form onSubmit={submit} noValidate>
        {error?.atCap && (
          <p className="mb-4 text-sm">
            {/* C27 item 12, UAT report #25: the platform website's pricing
                page for this app, not the store's catalogue. */}
            <a href={plansUrl(APP_SLUG)} className="text-primary font-medium">
              See plans with more clients →
            </a>
          </p>
        )}

        <div className={FORM_GRID}>
          {/* The kit's radio group takes no `aria-required`; it always holds an
              answer, so the star is the whole of what is owed here. */}
          <FormRow label="Individual or business?" required>
            <RadioGroup
              legend="Individual or business?"
              hideLegend
              className="sm:flex-row sm:gap-6 sm:pt-3"
              value={draft.kind as 'individual' | 'business'}
              onChange={setKind}
              options={[
                { value: 'individual', label: 'Individual', description: 'A person.' },
                {
                  value: 'business',
                  label: 'Business',
                  description: 'A firm, company, trust or HUF.',
                },
              ]}
            />
          </FormRow>

          <FormRow
            label={draft.kind === 'individual' ? 'Full name' : 'Legal name'}
            htmlFor="new-client-name"
            required
          >
            <Input
              id="new-client-name"
              value={draft.legal_name}
              onChange={(e) => set({ legal_name: e.target.value })}
              required
              aria-required="true"
              maxLength={200}
              error={fieldError('legal_name')}
            />
          </FormRow>

          {constitutionRequired(draft.kind) && (
            <FormRow label="Constitution" htmlFor="new-client-constitution" required>
              <Select
                id="new-client-constitution"
                value={draft.constitution ?? ''}
                onChange={(e) => set({ constitution: e.target.value || null, company_type: null })}
                required
                aria-required="true"
                error={fieldError('constitution')}
                hint={
                  draft.constitution === 'proprietorship'
                    ? 'A proprietorship trades on the proprietor’s own PAN.'
                    : undefined
                }
              >
                {/* A placeholder for a required answer, not a choice (#13). */}
                <option value="" disabled hidden>
                  Choose…
                </option>
                {constitutions.map((value) => (
                  <option key={value} value={value}>
                    {CONSTITUTION_LABELS[value] ?? value}
                  </option>
                ))}
              </Select>
            </FormRow>
          )}

          {companyTypeApplies(draft.kind, draft.constitution) && (
            <FormRow label="Company type" htmlFor="new-client-company-type">
              <Select
                id="new-client-company-type"
                value={draft.company_type ?? ''}
                onChange={(e) => set({ company_type: e.target.value || null })}
              >
                <option value="">Not stated</option>
                {companyTypes.map((value) => (
                  <option key={value} value={value}>
                    {COMPANY_TYPE_LABELS[value] ?? value}
                  </option>
                ))}
              </Select>
            </FormRow>
          )}

          <FormRow label="PAN" htmlFor="new-client-pan">
            <Input
              id="new-client-pan"
              value={draft.pan}
              onChange={(e) => set({ pan: e.target.value })}
              maxLength={10}
              placeholder="AAAPA1234A"
              className="font-mono uppercase"
              error={fieldError('pan')}
              hint={panHint}
            />
          </FormRow>

          <FormRow label="GSTIN" htmlFor="new-client-gstin">
            <Input
              id="new-client-gstin"
              value={draft.gstin}
              onChange={(e) => set({ gstin: e.target.value })}
              maxLength={15}
              placeholder="27AAAPA1234A1Z5"
              className="font-mono uppercase"
              error={fieldError('gstin')}
              hint={gstinHint}
            />
          </FormRow>

          <FormRow label="Tags" htmlFor="new-client-tags">
            <Input
              id="new-client-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="retainer, gst"
              hint="Comma separated."
            />
          </FormRow>
        </div>

        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} loadingText="Adding…">
            Add client
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
